// HAVING add/remove planner. Structurally mirrors the WHERE planner
// (applyStructuralEdit.where.ts): HAVING fragments are joined by `AND`/`OR`
// combinators; the slice contract preserves the user's existing combinator
// style by re-emitting `<combinator> <text>` after the last fragment when
// fragments already exist (wrapping a clause joined by the other connector and
// parenthesising a multi-part predicate, exactly like WHERE), and synthesizes a
// fresh ` HAVING <text>` clause when none does. A synthesized HAVING lands after
// GROUP BY (or WHERE / FROM-JOIN tail) and BEFORE ORDER BY / LIMIT / set-op.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — add/remove.

import type { PlannedByteEdit, StructuralEdit } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import {
  CLAUSE_BOUNDARIES_AFTER_HAVING,
  deleteEdit,
  findClauseInsertionPoint,
  findKeyword,
  insertEdit,
  pickClauseInsertOffset,
  scanStatementKeywords,
} from './applyStructuralEdit.shared';
import { planAppendPredicate } from './applyStructuralEdit.where';
import { outsideLineComments, parenthesizeCompound } from './wholePredicate';

export function planHavingEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-having' | 'remove-having' | 'clear-having' }>,
): readonly PlannedByteEdit[] {
  if (edit.op === 'add-having') return planAddHaving(ctx, edit);
  if (edit.op === 'clear-having') return planClearHaving(ctx);
  return planRemoveHaving(ctx, edit);
}

/** "Clear all" (report G6) — mirrors `planClearWhere`; see its comment. */
function planClearHaving(ctx: EditCtx): readonly PlannedByteEdit[] {
  const having = ctx.structure.having ?? [];
  if (having.length === 0) return [];
  const kws = scanStatementKeywords(ctx);
  const havingKw = findKeyword(kws, 'HAVING');
  const start = havingKw ? havingKw.start : having[0].range.startOffset;
  const end = having[having.length - 1].range.endOffset;
  return [deleteEdit(ctx, start, end, 'clear-having')];
}

function planAddHaving(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-having' }>,
): readonly PlannedByteEdit[] {
  const having = ctx.structure.having ?? [];
  const text = parenthesizeCompound(edit.text, ctx.options);
  if (having.length > 0) {
    return planAppendPredicate(ctx, having, edit.combinator, text, 'add-having');
  }
  // No HAVING clause yet — synthesize one. Anchor to the end of GROUP BY (or
  // WHERE, or the FROM/JOIN tail); insert ` HAVING <text>` before the next
  // clause-boundary keyword (WINDOW / ORDER BY / LIMIT / set-op).
  const after = havingAnchor(ctx);
  const kws = scanStatementKeywords(ctx);
  const insertAt = findClauseInsertionPoint(
    kws, after, CLAUSE_BOUNDARIES_AFTER_HAVING, ctx.statementInfo.range.endOffset,
  );
  const trimmedAt = pickClauseInsertOffset(
    ctx.source, after, insertAt, ctx.statementInfo.range.startOffset,
  );
  return [insertEdit(ctx, trimmedAt, outsideLineComments(ctx, trimmedAt, trimmedAt, ` HAVING ${text}`), 'add-having:empty')];
}

function planRemoveHaving(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'remove-having' }>,
): readonly PlannedByteEdit[] {
  const having = ctx.structure.having ?? [];
  if (edit.fragmentIndex < 0 || edit.fragmentIndex >= having.length) return [];
  if (having.length === 1) {
    // Remove the only fragment AND the leading HAVING keyword. Find HAVING.
    const kws = scanStatementKeywords(ctx);
    const havingKw = findKeyword(kws, 'HAVING');
    if (havingKw) {
      return [deleteEdit(
        ctx, havingKw.start, having[0].range.endOffset, 'remove-having:with-keyword',
      )];
    }
    return [deleteEdit(
      ctx, having[0].range.startOffset, having[0].range.endOffset, 'remove-having:fragment-only',
    )];
  }
  // Multiple fragments — drop one, with its adjacent AND/OR.
  const idx = edit.fragmentIndex;
  let start: number; let end: number;
  if (idx === 0) {
    // Drop fragment[0] + the AND/OR before fragment[1].
    start = having[0].range.startOffset;
    end = having[1].range.startOffset;
  } else {
    // Drop the AND/OR before fragment[idx] + fragment[idx].
    start = having[idx - 1].range.endOffset;
    end = having[idx].range.endOffset;
  }
  return [deleteEdit(ctx, start, end, 'remove-having')];
}

// Find the byte offset that's the natural anchor for a synthesized HAVING
// clause: end of the last GROUP BY item, or end of the last WHERE fragment, or
// end of the last JOIN / FROM item / select-list item (mirrors whereAnchor's
// tail fallbacks with the two clause anchors HAVING sits after prepended).
function havingAnchor(ctx: EditCtx): number {
  const { structure } = ctx;
  if (structure.groupBy?.length) {
    return structure.groupBy[structure.groupBy.length - 1].range.endOffset;
  }
  if (structure.where?.length) {
    return structure.where[structure.where.length - 1].range.endOffset;
  }
  if (structure.joins?.length) {
    return structure.joins[structure.joins.length - 1].range.endOffset;
  }
  if (structure.fromItems?.length) {
    return structure.fromItems[structure.fromItems.length - 1].range.endOffset;
  }
  if (structure.selectItems?.length) {
    return structure.selectItems[structure.selectItems.length - 1].range.endOffset;
  }
  return ctx.statementInfo.range.startOffset;
}
